import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment';
import FormTypes from 'common/Forms/FormTypes';

import { getStorageSetting, getWhdStatementFromAccount, checkTin, clearPdfSummaryDownload } from 'actions';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

import FormReviewWorkflow from 'components/FormReviewWorkflow';
import ValidationResultsSection from 'components/ValidationResultsSection';
import SupportingDocs from 'components/SupportingDocs';
import FormReviewerComments from 'components/FormReviewerComments';
import IndividualValidationOverrides from 'components/IndividualValidationOverrides';
import UploadWithholdingSection from 'components/UploadWithholdingSection';
import WithholdingRatesModal from 'components/Modals/WithholdingRatesModal';
import FormOverrideModal from 'components/Modals/FormOverrideModal';
import ConfirmModal from 'components/Modals/ConfirmModal';
import FormReviewHeader from 'components/FormReviewHeader';
import HintButton from 'components/Common/Buttons/HintButton';
import FormWorkflowStages from 'common/Workflows/FormWorkflowStages';
import TinCheckStatus from 'components/TinCheckStatus';

import FormReviewPanelSections from './FormReviewPanelSections';

function usePrevious(value) {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const FormReviewPanel = ({
  formId, formType, isOnline, hasParentAccount, accountId, isEnabled, isLocked, isCollapsed, viewImyTree, viewWithholding, onOpenWithholdingModal, onCloseWithholdingModal, onCollapseExpand, className,
  dispatch, permissions, settings, isLoading, isValidatingForm, validationResult, formReviewState, workflowState, supportingDocs, isAccountWithholdingShown, isFormOverrideModalShown,
  onConfirmFormOverride, onOpenFormOverrideModal, onCloseFormOverrideModal,
  onOpenCancelFormOverrideModal, onCloseCancelFormOverrideModal, isCancelFormOverrideModalShown,
  isSavingOverrides, isAddingStatement, isCreatingNewWhd, accountWithholdingStatement,
  isFormDirty, piiValidationCodes, tinMatchResult, tinMatchFormProps, formSummaryData, isDownloadingFormSummary, downloadFormSummaryError
}) => {
  // eslint-disable-next-line
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedSection, setSelectedSection] = useState(FormReviewPanelSections.Summary);
  const [sectionShown, setSectionShown] = useState(!isCollapsed);
  const [contextInfoShown, setContextInfoShown] = useState(false);
  const [pdfSummaryDownload, setPdfSummaryDownload] = useState(null);

  const prevSelectedResult = usePrevious(selectedResult);

  const piiEnabled = settings && settings.selectedClient?.isPiiProtectionEnabled;

  React.useEffect(() => {
    setSectionShown(!isCollapsed);
  }, [isCollapsed]);

  React.useEffect(() => {
    setContextInfoShown(false);
  }, [formReviewState, workflowState]);

  React.useEffect(() => {
    if (prevSelectedResult && prevSelectedResult.id === 0) {
      const nonZeroResults = validationResult.result.filter(v => v.id !== 0);
      if (nonZeroResults.length === 0) {
        return;
      }
    } else if (selectedResult != null && validationResult != null && validationResult.result != null) {
      const result = validationResult.result.filter(v => v.id === selectedResult.id);

      if (result != null && result.length > 0) {
        setSelectedResult(result[0]);
        setSelectedAccountId(result[0].accountId);
        return;
      }
    }
    setSelectedResult(null);
    setSelectedAccountId(null);
    setSelectedSection(FormReviewPanelSections.Summary);
  }, [validationResult]);

  React.useEffect(() => {
    if (validationResult && accountId && accountId > 0) {
      const result = validationResult.result.filter(v => v.accountId === accountId);
      if (result != null && result.length > 0) {
        setSelectedAccountId(accountId);
        setSelectedResult(result[0]);
        setSelectedSection(FormReviewPanelSections.ValidationResults);
      }
    }
  }, [accountId]);

  const hasReviewerPermission = isEnabled && AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer, ActivityPerms.fwfSecondaryReviewer, ActivityPerms.fwfExpertReviewer, ActivityPerms.fwfSupplReviewer]);
  const hasSupportingDocsPermission = isEnabled && AccessControl.hasPermission(permissions, [ActivityPerms.scannedSupDocs, ActivityPerms.scannedSupDocsInternal]);
  const hasWithholdingPermission = isEnabled && settings && settings.showWithholding && AccessControl.hasPermission(permissions, [ActivityPerms.whdViewRates]);
  const hasViewTreePermission = isEnabled && AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree])
    && ((formType.toLowerCase().startsWith('w') && selectedResult?.parentAccountId) || (!selectedResult?.parentAccountId && formType.toLowerCase().startsWith('w8imy')));
  const canApplyFormOverride = !isLocked && isEnabled && hasReviewerPermission
    && (validationResult && (validationResult.formOverrideApplied || validationResult.valid))
    && formReviewState != null && formReviewState.workflowStage !== FormWorkflowStages.Complete && formReviewState.workflowStage !== FormWorkflowStages.BatchStaging
    && workflowState != null && workflowState.assignedUser === getStorageSetting('username')
    && AccessControl.hasPermission(permissions, [ActivityPerms.overrideValidation]);
  const hasIntermediaryToolsPermission = isEnabled && AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrViewWhs, ActivityPerms.multiTierMgrEditWhs, ActivityPerms.multiTierMgrCreateWhs]);
  const canShowWithholdingStatementSection = (formType.toLowerCase().startsWith('w8imy') || accountWithholdingStatement != null) && hasIntermediaryToolsPermission && selectedAccountId != null;

  const getSectionClass = tab => (tab === selectedSection ? 'active' : '');

  const getSupportingDocsBadgeCount = () => {
    const docCount = supportingDocs ? supportingDocs.length : 0;
    let badgeCount = '0';
    if (docCount) {
      badgeCount = docCount > 99 ? '99+' : docCount.toString();
    }
    return badgeCount;
  };

  const getFormValidState = (result) => {
    if (!result) {
      return 'validation-result-icon none';
    }
    if (result.valid != null && result.valid !== 'undefined') {
      return `validation-result-icon ${ ((result.valid) ? 'valid' : 'invalid') }`;
    }
    return '';
  };

  const getSupportingDocsBadgeClass = () => {
    let badgeClass = 'supporting-docs-icon';
    if (supportingDocs && supportingDocs.length) {
      badgeClass = 'supporting-docs-icon bluebadge badge';
    }
    badgeClass = `${ badgeClass }`;
    return badgeClass;
  };

  const getAccountIconCssClass = (result) => {
    if (!result.isValidated) {
      return 'icon-not-validated';
    }
    let cssClass = result.valid && (result.childResults == null || result.childResults.filter(r => !r.valid).length === 0) ? 'icon-valid' : 'icon-invalid';
    cssClass += (result.warnings && result.warnings.filter(w => w.code.indexOf('WRN') > -1).length > 0)
      || (result.childResults && result.childResults.filter(r => !r.warnings && r.warnings.filter(w => w.code.indexOf('WRN') > -1).length > 0).length > 0) ? ' red-flag' : '';
    return cssClass;
  };

  const isExpired = expiryDate => moment().diff(moment(expiryDate).endOf('day')) > 0;

  const isValid = result => result.valid && (result.childResults == null || result.childResults.filter(r => !r.valid).length === 0);

  const getAccountDescription = (result, formFamily) => {
    let validStatus = 'Not Validated';
    if (result.isValidated) {
      if (isExpired(result.validUntil) && isValid(result)) {
        validStatus = `Expired on ${ moment(result.validUntil).format('ll') }`;
      } else {
        validStatus = isValid(result) ? 'Valid' : 'Invalid';
        if (isValid(result) && result.workflowStage && result.workflowStage === FormWorkflowStages.Complete) {
          validStatus += ' (Not Expired)';
        }
        if (isValid(result) && result.isWithTreaty) {
          validStatus += ' with Treaty';
        }
        if (result.isLocked) {
          validStatus += ' & Locked';
        }
      }
    }
    return `${ formFamily }: ${ !result.accountNumber ? 'N/A' : result.accountNumber } - ${ validStatus }`;
  };

  const navigateToImyStructure = () => {
    dispatch(push(`/w8imystructure/${ selectedAccountId }`));
  };

  const navigateToAccountValidationResult = (result) => {
    setSelectedAccountId(result.accountId);
    setSelectedSection(FormReviewPanelSections.ValidationResults);
    setSelectedResult(result);
    setContextInfoShown(false);
  };

  const navigateToAccountSummary = () => {
    setSelectedAccountId(null);
    setSelectedSection(FormReviewPanelSections.Summary);
    setSelectedResult(null);
    setContextInfoShown(false);
  };

  const onSectionCollapseExpand = () => {
    setSectionShown(!sectionShown);
    if (onCollapseExpand) {
      onCollapseExpand(!sectionShown);
    }
  };

  const onHeaderResultSelect = (id) => {
    if (!validationResult || !validationResult.result) {
      return;
    }

    if (!id) {
      navigateToAccountSummary();
    }

    const matched = validationResult.result.filter(o => o.id === id || o.accountId === id);
    if (matched && matched.length > 0) {
      navigateToAccountValidationResult(matched[0]);
    }
  };

  const onFormOverrideChanged = (checked) => {
    if (checked) {
      validationResult.formOverrideApplied = true;
      onOpenFormOverrideModal();
    } else {
      validationResult.formOverrideApplied = false;
      onOpenCancelFormOverrideModal();
    }
  };

  const showTinCheckStatus = () => {
    if (!tinMatchFormProps || !tinMatchFormProps.tin || !tinMatchFormProps.name) {
      return false;
    }
    const tinIsAppliedForOrEmpty = tinMatchFormProps.tin.appliedForTin || ((!tinMatchFormProps.tin.ssn && !tinMatchFormProps.tin.ein));

    const tinIsComplete = tinMatchFormProps.tin.ssn?.length === 9 || tinMatchFormProps.tin.ein?.length === 9;

    return formType.toLowerCase().startsWith('w9') && !piiEnabled && settings.showTinMatching && !tinIsAppliedForOrEmpty && tinIsComplete;
  };

  const onCheckTin = (_formId, updateExisting) => {
    dispatch(checkTin(_formId, updateExisting));
  };

  useEffect(() => {
    if (selectedAccountId && (formType.toLowerCase().startsWith('w8imy') || hasParentAccount) && hasIntermediaryToolsPermission && !isCreatingNewWhd) {
      dispatch(getWhdStatementFromAccount(selectedAccountId));
    }
  }, [accountId, isAddingStatement, isCreatingNewWhd, selectedAccountId]);

  useEffect(() => () => {
    if (pdfSummaryDownload) {
      URL.revokeObjectURL(pdfSummaryDownload);
    }
    dispatch(clearPdfSummaryDownload());
  }, []);

  useEffect(() => {
    if (formSummaryData && !isDownloadingFormSummary && !downloadFormSummaryError) {
      setPdfSummaryDownload(formSummaryData);
      const link = document.createElement('a');
      link.href = formSummaryData;
      link.setAttribute('download', 'validationsummary.pdf');
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
    }
  }, [formSummaryData]);

  const accountSelected = selectedAccountId !== null;
  const formFamily = formType && FormTypes[formType.toLowerCase()] && FormTypes[formType.toLowerCase()].getFamilyName();

  return (<div className={ className }>
    { !isLoading && <div className={ `section-content ${ !sectionShown ? 'hide-content' : '' }` }>
      { validationResult && validationResult.result && <FormReviewHeader
        results={ validationResult.result }
        customerDbId={ validationResult.result.length > 0 && validationResult.result[0].customerDbId }
        customerId={ validationResult.result.length > 0 && validationResult.result[0].customerId }
        customerName={ validationResult.result.length > 0 && validationResult.result[0].customerName }
        selectedResultId={ selectedResult && (selectedResult.id !== 0 ? selectedResult.id : selectedResult.accountId) }
        onResultSelect={ id => onHeaderResultSelect(id) }
      /> }
      { selectedSection === FormReviewPanelSections.Summary && <div className="form-review-summary">
        <h1>VALIDATION SUMMARY&nbsp;<HintButton hintType="hint" active={ contextInfoShown } onClick={ () => { setContextInfoShown(!contextInfoShown); } } /></h1>
        { contextInfoShown && <span className="context-info">Overview of form validation per account context.</span> }
        { isValidatingForm && <div className="validating-progress">
          <p>Please wait while a validation process is performed.</p>
          <progress className="progress" max="100" />
        </div> }
        { (validationResult === null || validationResult.result === null || [...validationResult.result].length < 1)
          && <h2 className="none">Not Validated</h2> }
        { validationResult && validationResult.result && [...validationResult.result].length >= 1
          && <React.Fragment>
            <div className="summary-results">
              {showTinCheckStatus() && <TinCheckStatus
                onCheckTin={ (id, updateExisting) => onCheckTin(id, updateExisting) }
                formId={ formId }
                tinMatchResult={ tinMatchResult }
                workflowStage={ formReviewState && formReviewState.workflowStage }
                hasValidation={ validationResult && validationResult.result[0]?.id > 0 }
                tinMatchFormProps={ tinMatchFormProps }
                isFormDirty={ isFormDirty }
              />}
              <label>Associated Accounts:</label>
              { !isValidatingForm && [...validationResult.result].map((x, i) => x != null && (<button
                type="button"
                key={ validationResult.result[i].accountId }
                className={ getAccountIconCssClass(validationResult.result[i]) }
                onClick={ () => navigateToAccountValidationResult(validationResult.result[i]) }
              >{getAccountDescription(validationResult.result[i], formFamily) }</button>)) }
            </div>
            <div className="form-review-actions">
              <form className="form-horizontal side-bar-form form-override-action">
                <div className="form-group">
                  <div className="col-4 col-sm-12">
                    <label className="unpadded-label">Workflow</label>
                  </div>
                  <div className="col-8 col-sm-12">
                    { workflowState.workflowDescription }
                  </div>
                </div>
                <br />
                <div className="form-group">
                  <div className="col-4 col-sm-12">
                    <label htmlFor="cb-form-override" className="form-label">Validation Override</label>
                  </div>
                  <div>
                    <label className="form-checkbox">
                      Check this box to invalidate the form for all accounts
                      <input
                        type="checkbox"
                        id="cb-form-override"
                        name="formOverride"
                        value="true"
                        disabled={ !canApplyFormOverride }
                        checked={ validationResult.formOverrideApplied }
                        onChange={ e => onFormOverrideChanged(e.target.checked) }
                      />
                      <i className="form-icon" />
                    </label>
                  </div>
                </div>
              </form>
              <FormReviewWorkflow
                formId={ formId }
                formReviewState={ formReviewState }
                validReviewer={ !isLocked && hasReviewerPermission }
                isLocked={ isLocked || !isEnabled }
                hasValidationResult={ validationResult != null }
                isFormDirty={ isFormDirty }
              />
            </div>
          </React.Fragment> }
      </div> }
      { selectedSection === FormReviewPanelSections.SupportingDocs && <SupportingDocs
        workflowStage={ formReviewState && formReviewState.workflowStage }
        formId={ formId }
        documents={ supportingDocs }
        workflowUsername={ workflowState && workflowState.assignedUser }
        formType={ formType }
        isLocked={ isLocked || !isEnabled }
      /> }
      { selectedSection === FormReviewPanelSections.ValidationResults && <ValidationResultsSection
        validationResult={ selectedResult }
        whdResult={ validationResult?.whdResult }
        isValidatingForm={ isValidatingForm }
        isValid={ selectedResult?.valid }
        isOnlineForm={ isOnline }
        showWitholdingValidation={ formType.toLowerCase().startsWith('w8imy') && validationResult != null && validationResult.whdResult != null }
        piiValidationCodes={ piiValidationCodes }
        isPiiProtectionEnabled={ settings.selectedClient.isPiiProtectionEnabled }
        tinMatchResult={ tinMatchResult }
        onCheckTin={ (id, updateExisting) => onCheckTin(id, updateExisting) }
        showTinMatching={ settings.showTinMatching }
        tinMatchFormProps={ tinMatchFormProps }
        isFormDirty={ isFormDirty }
        isDownloadingFormSummary={ isDownloadingFormSummary }
        permissions={ permissions }
      /> }
      { selectedSection === FormReviewPanelSections.ReviewerComments && validationResult && <FormReviewerComments
        formId={ formId }
        accountId={ selectedAccountId }
        formReviewState={ formReviewState }
        validReviewer={ hasReviewerPermission }
        isLocked={ isLocked || !isEnabled }
        hasValidationResult={ validationResult != null }
      /> }
      { selectedSection === FormReviewPanelSections.Overrides && validationResult && <IndividualValidationOverrides
        permissions={ permissions }
        workflowStage={ formReviewState && formReviewState.workflowStage }
        workflowUsername={ workflowState && workflowState.assignedUser }
        formId={ selectedResult.formId }
        accountId={ selectedResult.accountId }
        customerId={ selectedResult.customerId }
        customerName={ selectedResult.customerName }
        formType={ selectedResult.formType }
        key={ selectedResult.controlId }
        isOnlineForm={ isOnline }
        valid={ selectedResult.valid }
        rejections={ selectedResult.rejections }
        warnings={ selectedResult.warnings }
        overrides={ selectedResult.overrides }
        isWithTreaty={ selectedResult.isWithTreaty }
        isLocked={ isLocked || !isEnabled }
        piiValidationCodes={ piiValidationCodes }
        isPiiProtectionEnabled={ settings.selectedClient.isPiiProtectionEnabled }
        showTinMatching={ settings.showTinMatching }
        tinMatchResult={ tinMatchResult }
        onCheckTin={ (id, updateExisting) => onCheckTin(id, updateExisting) }
        tinMatchFormProps={ tinMatchFormProps }
        isFormDirty={ isFormDirty }
      /> }
      { selectedSection === FormReviewPanelSections.WithholdingStatement && <UploadWithholdingSection
        accountId={ selectedAccountId }
        topAccountId={ accountWithholdingStatement?.account?.id }
        formId={ formId }
        isAddingStatement={ isAddingStatement }
        withholdingStatement={ (accountWithholdingStatement && accountWithholdingStatement.withholdingStatement) || null }
        aiJobExists={ accountWithholdingStatement?.aiJobExists }
        aiJobStatus={ accountWithholdingStatement?.aiJobStatus }
        dispatch={ dispatch }
        isFormDirty={ isFormDirty }
        assignedUser={ workflowState.assignedUser }
        whdValidationResult={ (validationResult && validationResult.whdResult) || null }
      /> }
    </div> }
    <div id="icon-bar" className="icon-bar">
      { (hasSupportingDocsPermission || hasReviewerPermission) && <div className={ getSectionClass(FormReviewPanelSections.Summary) }><button
        type="button"
        className={ `summary-icon ${ getSectionClass(FormReviewPanelSections.Summary) }` }
        onClick={ () => { setSelectedSection(FormReviewPanelSections.Summary); setSelectedResult(null); setSelectedAccountId(null); } }
        title="Validation Summary"
      >&nbsp;</button></div> }
      { (hasSupportingDocsPermission || hasReviewerPermission) && <div className={ getSectionClass(FormReviewPanelSections.SupportingDocs) }><button
        type="button"
        className={ getSupportingDocsBadgeClass() }
        onClick={ () => setSelectedSection(FormReviewPanelSections.SupportingDocs) }
        title="Supporting Documents"
        data-badge={ getSupportingDocsBadgeCount() }
      >&nbsp;</button></div> }
      { isEnabled && accountSelected && <div className={ getSectionClass(FormReviewPanelSections.ValidationResults) }><button
        id="validation-results-btn"
        type="button"
        className={ getFormValidState(selectedResult) }
        onClick={ () => setSelectedSection(FormReviewPanelSections.ValidationResults) }
        title="Validation Results"
      >&nbsp;</button></div> }
      { hasReviewerPermission && accountSelected && <div className={ getSectionClass(FormReviewPanelSections.ReviewerComments) }><button
        type="button"
        className={ `form-review-icon ${ getSectionClass(FormReviewPanelSections.ReviewerComments) }` }
        onClick={ () => setSelectedSection(FormReviewPanelSections.ReviewerComments) }
        title="Reviewer Comments"
      >&nbsp;</button></div> }
      { isEnabled && hasReviewerPermission && validationResult && validationResult.result && accountSelected && <div className={ getSectionClass(FormReviewPanelSections.Overrides) }><button
        type="button"
        className={ `override-icon ${ getSectionClass(FormReviewPanelSections.Overrides) }` }
        onClick={ () => setSelectedSection(FormReviewPanelSections.Overrides) }
        title="Validation Overrides"
      >&nbsp;</button></div> }
      { hasViewTreePermission && viewImyTree && accountSelected && <div><button
        type="button"
        className="imy-tree-icon"
        onClick={ () => navigateToImyStructure() }
        title="W-8IMY Structure"
      >&nbsp;</button></div> }
      { hasWithholdingPermission && viewWithholding && accountSelected && <div><button
        type="button"
        className="withholding-rates-icon"
        onClick={ () => onOpenWithholdingModal(selectedAccountId) }
        title="Show Withholding Rates"
      >&nbsp;</button></div> }
      { canShowWithholdingStatementSection && <div><button
        type="button"
        className="whd-statement-icon"
        onClick={ () => setSelectedSection(FormReviewPanelSections.WithholdingStatement) }
        title="Withholding Statement"
      >&nbsp;</button></div> }
      { isEnabled && <div className={ sectionShown ? 'collapse-icon' : 'expand-icon' }><button
        type="button"
        onClick={ () => onSectionCollapseExpand() }
        title="Click to expand/collapse"
      >&nbsp;</button></div> }
    </div>
    <WithholdingRatesModal
      isShown={ isAccountWithholdingShown }
      onClose={ () => onCloseWithholdingModal() }
    />
    { isFormOverrideModalShown && !isLocked && <FormOverrideModal
      isShown={ isFormOverrideModalShown }
      onOverrideConfirm={ data => onConfirmFormOverride(data) }
      onClose={ () => {
        validationResult.formOverrideApplied = false;
        onCloseFormOverrideModal();
      } }
    /> }
    { isCancelFormOverrideModalShown && !isLocked && <ConfirmModal
      key="cancelFormOverride"
      isShown={ isCancelFormOverrideModalShown }
      isPerformingAction={ isSavingOverrides }
      onConfirm={ () => onConfirmFormOverride() }
      onCancel={ () => {
        validationResult.formOverrideApplied = true;
        onCloseCancelFormOverrideModal();
      } }
      title="Confirm Form Override Cancellation"
      message="Are you sure you want to cancel this invalidated form override?"
    /> }
  </div>);
};

FormReviewPanel.propTypes = {
  dispatch: PropTypes.func.isRequired,
  formId: PropTypes.number.isRequired,
  formType: PropTypes.string.isRequired,
  isOnline: PropTypes.bool.isRequired,
  isEnabled: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool,
  isCollapsed: PropTypes.bool.isRequired,
  onOpenWithholdingModal: PropTypes.func.isRequired,
  onCloseWithholdingModal: PropTypes.func.isRequired,
  onConfirmFormOverride: PropTypes.func.isRequired,
  onOpenFormOverrideModal: PropTypes.func.isRequired,
  onCloseFormOverrideModal: PropTypes.func.isRequired,
  onOpenCancelFormOverrideModal: PropTypes.func.isRequired,
  onCloseCancelFormOverrideModal: PropTypes.func.isRequired,
  accountId: PropTypes.number,
  hasParentAccount: PropTypes.bool,
  viewImyTree: PropTypes.bool,
  viewWithholding: PropTypes.bool,
  className: PropTypes.string,
  settings: PropTypes.shape(),
  permissions: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool,
  hasError: PropTypes.shape({
    code: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    message: PropTypes.string
  }),
  isValidatingForm: PropTypes.bool,
  validationResult: PropTypes.shape(),
  formReviewState: PropTypes.shape(),
  workflowState: PropTypes.shape(),
  supportingDocs: PropTypes.arrayOf(PropTypes.shape()),
  isAccountWithholdingShown: PropTypes.bool,
  onCollapseExpand: PropTypes.func,
  isFormOverrideModalShown: PropTypes.bool,
  isCancelFormOverrideModalShown: PropTypes.bool,
  isSavingOverrides: PropTypes.bool,
  isAddingStatement: PropTypes.bool,
  isCreatingNewWhd: PropTypes.bool,
  accountWithholdingStatement: PropTypes.shape(),
  isFormDirty: PropTypes.bool,
  piiValidationCodes: PropTypes.shape(),
  tinMatchResult: PropTypes.arrayOf(PropTypes.shape()),
  tinMatchFormProps: PropTypes.shape(),
  formSummaryData: PropTypes.string,
  isDownloadingFormSummary: PropTypes.bool,
  downloadFormSummaryError: PropTypes.string,
};

FormReviewPanel.defaultProps = {
  accountId: null,
  hasParentAccount: false,
  viewImyTree: false,
  viewWithholding: false,
  className: null,
  settings: null,
  permissions: null,
  isLoading: true,
  hasError: null,
  isValidatingForm: false,
  validationResult: null,
  formReviewState: null,
  workflowState: null,
  supportingDocs: null,
  isAccountWithholdingShown: false,
  onCollapseExpand: null,
  isFormOverrideModalShown: false,
  isCancelFormOverrideModalShown: false,
  isSavingOverrides: false,
  isAddingStatement: false,
  isCreatingNewWhd: false,
  accountWithholdingStatement: null,
  isFormDirty: false,
  isLocked: false,
  piiValidationCodes: null,
  tinMatchResult: null,
  tinMatchFormProps: null,
  formSummaryData: null,
  isDownloadingFormSummary: false,
  downloadFormSummaryError: null,
};

function mapStateToProps(state) {
  const {
    auth: {
      settings,
      permissions,
      piiValidationCodes
    },
    forms: {
      isValidatingForm,
      validationResult,
      isGettingFormById,
      formByIdError,
      supportingDocs,
      formReviewState,
      workflowState,
      isAccountWithholdingShown,
      isFormOverrideModalShown,
      isCancelFormOverrideModalShown,
      isSavingOverrides,
      isAddingStatement,
      isCreatingNewWhd,
      accountWithholdingStatement: accountWithholdingStatementList,
      isDownloadingFormSummary,
      formSummaryData,
      downloadFormSummaryError
    }
  } = state;

  const accountWithholdingStatement = (accountWithholdingStatementList && accountWithholdingStatementList.length > 0) ? accountWithholdingStatementList[0] : null;

  return {
    settings,
    permissions,
    isValidatingForm,
    validationResult,
    isLoading: isGettingFormById,
    hasError: formByIdError,
    supportingDocs,
    formReviewState,
    workflowState,
    isAccountWithholdingShown,
    isFormOverrideModalShown,
    isCancelFormOverrideModalShown,
    isSavingOverrides,
    isAddingStatement,
    isCreatingNewWhd,
    accountWithholdingStatement,
    piiValidationCodes,
    isDownloadingFormSummary,
    formSummaryData,
    downloadFormSummaryError
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(FormReviewPanel);
